import { useRef, useState } from "react";
import Slider from "react-slick";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CardMedia,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { LocationOn } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";

const isVideo = (url) => {
  if (typeof url !== "string") return false;
  return /\.(mp4|webm|mkv|mov|avi)(\?.*)?$/i.test(url);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// ─── Reusable Video Tile ────────────────────────────────────────────────────
function VideoTile({ src, height, onOpenPreview, cardMode = true }) {
  const videoRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleToggle = (e) => {
    e.stopPropagation();
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.paused) {
      vid.play();
      setPlaying(true);
    } else {
      vid.pause();
      setPlaying(false);
    }
  };

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ position: "relative", height, width: "100%" }}
    >
      {/* Skeleton until video metadata loads */}
      {!loaded && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{ position: "absolute", top: 0, left: 0, borderRadius: 4 }}
        />
      )}

      <CardMedia
        component="video"
        ref={videoRef}
        src={src}
        playsInline
        preload="auto"           // ← was "metadata"; auto loads enough to show first frame
        disablePictureInPicture
        disableRemotePlayback
        controlsList="nopictureinpicture nodownload"
        onLoadedData={() => setLoaded(true)}
        onClick={(e) => {
          e.stopPropagation();
          handleToggle(e);       // always toggle play/pause on click — no dialog open
        }}
        sx={{
          height,
          borderRadius: 4,
          objectFit: "cover",
          cursor: "pointer",
          display: loaded ? "block" : "none",
          transition: "transform 0.3s ease",
          "&:hover": { transform: "scale(1.04)" },
        }}
      />

      {/* Play button — when paused */}
      {loaded && !playing && (
        <IconButton
          onClick={handleToggle}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.4)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
            pointerEvents: "all",
          }}
        >
          <PlayArrowIcon sx={{ fontSize: 40 }} />
        </IconButton>
      )}

      {/* Pause button — when playing and card is hovered */}
      {loaded && playing && hovered && (
        <IconButton
          onClick={handleToggle}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.4)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
            pointerEvents: "all",
          }}
        >
          <PauseIcon sx={{ fontSize: 40 }} />
        </IconButton>
      )}

      {/* Expand icon — bottom-right, only in card mode */}
      {cardMode && loaded && (
        <IconButton
          onClick={(e) => { e.stopPropagation(); onOpenPreview?.(); }}
          size="small"
          sx={{
            position: "absolute",
            bottom: 6,
            right: 6,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.45)",
            p: "3px",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: 13 }} />
        </IconButton>
      )}
    </Box>
  );
}

// ─── Reusable Image Tile ────────────────────────────────────────────────────
function ImageTile({ src, alt, height, onClick }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <Box sx={{ position: "relative", height, width: "100%" }}>
      {!loaded && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{ position: "absolute", top: 0, left: 0, borderRadius: 4 }}
        />
      )}
      <CardMedia
        component="img"
        image={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onClick={(e) => { e.stopPropagation(); onClick?.(); }}
         
        sx={{
          height,
          borderRadius: 4,
          objectFit: "cover",
          cursor: "zoom-in",
          display: loaded ? "block" : "none",
          transition: "transform 0.3s ease, opacity 0.5s ease-in-out",
          opacity: loaded ? 1 : 0,
          "&:hover": { transform: "scale(1.04)" },
        }}
      />
    </Box>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function PlotSlider({
  id,
  name,
  subText,
  image,
  images = [],
  isHome,
  totalPrice,
  postedOn,
  lastUpdate,
  verified,
  area,
  address,
  isGeo,
  soldStatus,
  sold_status,
  card,
}) {
  const isSold = soldStatus === 1 || sold_status === 1 || card?.sold_status === 1;
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const previewSliderRef = useRef(null);
  const videoRefs = useRef({});
  const [previewPlaying, setPreviewPlaying] = useState({});

  const [imgOpen, setImgOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [previewLoaded, setPreviewLoaded] = useState({});

  // Build media list
  const allImages =
    Array.isArray(images) && images.length > 0
      ? images
      : image
        ? [image]
        : ["/assets/images/placeholder_img.png"];

  const showNavArrows = allImages.length > 1;

  const CARD_HEIGHT = { xs: 150, sm: 170, md: 180 };
  const PREVIEW_HEIGHT = { xs: 300, sm: 480, md: 600 };

  const cardSliderSettings = {
    dots: false,
    arrows: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: false,
    infinite: false,
  };

  const handleOpenPreview = (index = 0) => {
    setActiveIndex(index);
    setImgOpen(true);
    setTimeout(() => {
      previewSliderRef.current?.slickGoTo(index);
    }, 100);
  };

  const handleCardClick = () => {
    navigate(`/plot-marketplace/details/${id}`);
  };

  return (
    <>
      {/* ── FULL PREVIEW DIALOG ── */}
      <Dialog
        open={imgOpen}
        onClose={() => setImgOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden", bgcolor: "rgba(0,0,0,0.9)" },
        }}
      >
        <IconButton
          onClick={() => setImgOpen(false)}
          sx={{
            position: "absolute",
            top: { xs: 10, sm: 16 },
            right: { xs: 10, sm: 20 },
            zIndex: 10,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.4)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              position: "relative",
              height: PREVIEW_HEIGHT,
              bgcolor: "transparent",
              overflow: "hidden",
              "& .slick-slider, & .slick-list, & .slick-track, & .slick-slide > div": {
                height: "100% !important",
              },
            }}
          >
            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickPrev()}
                sx={{
                  position: "absolute", left: 12, top: "50%",
                  transform: "translateY(-50%)", zIndex: 3,
                  color: "#fff", bgcolor: "rgba(0,0,0,0.5)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            )}

            <Slider
              ref={previewSliderRef}
              initialSlide={activeIndex}
              beforeChange={(oldIndex, newIndex) => {
                const vid = videoRefs.current[oldIndex];
                if (vid) vid.pause();

                setPreviewPlaying((p) => ({ ...p, [oldIndex]: false }));
                setActiveIndex(newIndex);
              }}
              dots
              infinite={false}
              speed={400}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
            >
              {allImages.map((src, i) => (
                <Box key={i}>
                  <Box
                    sx={{
                      height: PREVIEW_HEIGHT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(0,0,0,0.7)",
                      p: 1,
                      position: "relative",
                    }}
                  >
                    {!previewLoaded[src] && (
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{
                          position: "absolute", top: 0, left: 0,
                          bgcolor: "rgba(255,255,255,0.1)",
                        }}
                      />
                    )}
                    {isVideo(src) ? (
                      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        <video
                          ref={(el) => (videoRefs.current[i] = el)}
                          src={src}
                          playsInline
                          preload="auto"
                          disablePictureInPicture
                          disableRemotePlayback
                          controlsList="nodownload nopictureinpicture"
                          onLoadedData={() =>
                            setPreviewLoaded((p) => ({ ...p, [src]: true }))
                          }
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: previewLoaded[src] ? "block" : "none",
                            cursor: "pointer",
                          }}
                        />

                        {/* PLAY */}
                        {!previewPlaying[i] && (
                          <IconButton
                            onClick={() => {
                              const vid = videoRefs.current[i];
                              vid.play();
                              setPreviewPlaying((p) => ({ ...p, [i]: true }));
                            }}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              color: "#fff",
                              bgcolor: "rgba(0,0,0,0.5)",
                            }}
                          >
                            <PlayArrowIcon sx={{ fontSize: 50 }} />
                          </IconButton>
                        )}

                        {/* PAUSE */}
                        {previewPlaying[i] && (
                          <IconButton
                            onClick={() => {
                              const vid = videoRefs.current[i];
                              vid.pause();
                              setPreviewPlaying((p) => ({ ...p, [i]: false }));
                            }}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              color: "#fff",
                              bgcolor: "rgba(0,0,0,0.5)",
                            }}
                          >
                            <PauseIcon sx={{ fontSize: 50 }} />
                          </IconButton>
                        )}
                      </Box>
                    ) : (
                      <img
                        src={src}
                        alt={`${name || ""} ${i + 1}`}
                        onLoad={() =>
                          setPreviewLoaded((p) => ({ ...p, [src]: true }))
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: previewLoaded[src] ? "block" : "none",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Slider>

            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickNext()}
                sx={{
                  position: "absolute", right: 12, top: "50%",
                  transform: "translateY(-50%)", zIndex: 3,
                  color: "#fff", bgcolor: "rgba(0,0,0,0.5)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            )}

            {showNavArrows && (
              <Box
                sx={{
                  position: "absolute", bottom: 12, left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: "rgba(0,0,0,0.5)", color: "#fff",
                  borderRadius: "12px", px: 1.5, py: 0.3,
                  fontSize: 12, pointerEvents: "none", zIndex: 3,
                }}
              >
                {activeIndex + 1} / {allImages.length}
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── CARD ── */}
      <Card
        onClick={handleCardClick}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          mx: { xs: 1, sm: 0 },
          my: isHome ? 1 : 0,
          p: 1.5,
          borderRadius: 5,
          boxShadow: "0px 4px 20px rgba(0,0,0,0.15)",
          overflow: "hidden",
          cursor: "pointer",
          transition: "transform 0.22s ease, box-shadow 0.22s ease",
          "&:hover": {
            transform: "translateY(-4px) scale(1.01)",
            boxShadow: "0px 12px 32px rgba(0,0,0,0.18)",
          },
        }}
      >
        {/* ── MEDIA SECTION ── */}
        <Box sx={{ position: "relative" }}>
          {isSold && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                left: 10,
                bgcolor: "#d32f2f",
                color: "#fff",
                px: 2,
                py: 0.6,
                borderRadius: 1.5,
                fontSize: 13,
                fontWeight: 800,
                zIndex: 3,
                textTransform: "uppercase",
                boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
                letterSpacing: "0.5px",
              }}
            >
              Sold Out
            </Box>
          )}
          <Box
            sx={{
              opacity: isSold ? 0.6 : 1,
              filter: isSold ? "grayscale(20%)" : "none",
            }}
          >
            {allImages.length > 1 ? (
              // Multiple media → slider with arrows
              <Box
                sx={{
                  position: "relative",
                  height: CARD_HEIGHT,
                  "& .slick-slider, & .slick-list, & .slick-track, & .slick-slide > div":
                    { height: "100% !important" },
                }}
              >
                <Slider ref={sliderRef} {...cardSliderSettings}>
                  {allImages.map((src, i) =>
                    isVideo(src) ? (
                      <Box key={i} sx={{ height: CARD_HEIGHT }}>
                        <VideoTile
                          src={src}
                          height={CARD_HEIGHT}
                          onOpenPreview={() => handleOpenPreview(i)}
                        />
                      </Box>
                    ) : (
                      <Box key={i} sx={{ height: CARD_HEIGHT }}>
                        <ImageTile
                          src={src}
                          alt={name}
                          height={CARD_HEIGHT}
                          onClick={() => handleOpenPreview(i)}
                        />
                      </Box>
                    )
                  )}
                </Slider>

                {/* Prev / Next arrows on card slider */}
                <IconButton
                  onClick={(e) => { e.stopPropagation(); sliderRef.current?.slickPrev(); }}
                  sx={{
                    position: "absolute", left: 4, top: "50%",
                    transform: "translateY(-50%)", zIndex: 2,
                    bgcolor: "rgba(255,255,255,0.7)", p: 0.5,
                    "&:hover": { bgcolor: "white" },
                    display: { xs: "none", sm: "flex" },
                  }}
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  onClick={(e) => { e.stopPropagation(); sliderRef.current?.slickNext(); }}
                  sx={{
                    position: "absolute", right: 4, top: "50%",
                    transform: "translateY(-50%)", zIndex: 2,
                    bgcolor: "rgba(255,255,255,0.7)", p: 0.5,
                    "&:hover": { bgcolor: "white" },
                    display: { xs: "none", sm: "flex" },
                  }}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                </IconButton>

                {/* "+N more" badge */}
                <Box
                  sx={{
                    position: "absolute", bottom: 8, right: 8,
                    bgcolor: "rgba(0,0,0,0.55)", color: "#fff",
                    fontSize: 11, fontWeight: 700,
                    borderRadius: "6px", px: 1, py: 0.3,
                    pointerEvents: "none", zIndex: 2,
                  }}
                >
                  +{allImages.length - 1} more
                </Box>
              </Box>
            ) : isVideo(allImages[0]) ? (
              // Single video
              <VideoTile
                src={allImages[0]}
                height={CARD_HEIGHT}
                onOpenPreview={() => handleOpenPreview(0)}
              />
            ) : (
              // Single image
              <ImageTile
                src={allImages[0]}
                alt={name}
                height={CARD_HEIGHT}
                onClick={() => handleOpenPreview(0)}
              />
            )}
          </Box>
        </Box>

        {/* ── CARD CONTENT ── */}
        <CardContent
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            px: { xs: 1, sm: 1.5 },
            opacity: isSold ? 0.65 : 1,
          }}
        >
          <Typography
            fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
            fontWeight={600}
            mb={0.5}
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: { xs: 2, sm: 1, lg: 1 },
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {name || ""}
          </Typography>

          <Box display="flex" alignItems="center" mt={1}>
            <Typography
              fontSize={{ xs: 11, sm: 12 }}
              color="#9C9C9C"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
              }}
            >
              {address || "Chennai, TamilNadu"}
            </Typography>
          </Box>
<Box
  display="flex"
  mt={1}
  justifyContent="space-between"
  sx={{
    borderTop: `${!isHome && "1px solid #d3d3d3"}`,
    borderBottom: `${!isHome && "1px solid #d3d3d3"}`,
    py: 1,
    gap: 0.5,
  }}
>
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Box display="flex" alignItems="center">
      <img src="/assets/images/rupee.png"  alt="rupee" style={{ flexShrink: 0, width: 12, height: 12 }} />
      <Typography
        fontSize={{ xs: 11, sm: 12, md: 13 }}
        color="primary.main"
        sx={{ ml: 0.5, mr: 0.5, wordBreak: "break-all" }}
        fontWeight={600}
      >
        {totalPrice || ""}
      </Typography>
      {isHome && <Typography fontSize={11} sx={{ flexShrink: 0 }}>Price</Typography>}
    </Box>
    {!isHome && <Typography fontSize={{ xs: 11, sm: 12, md: 13 }}>Price</Typography>}
  </Box>

  <Box sx={{ flexShrink: 0, textAlign: "right" }}>
    <Box display="flex" alignItems="center" justifyContent="flex-end">
      <img src="/assets/images/sqft.png" alt="sqft" style={{ flexShrink: 0, width: 20, height: 20 }} />
      <Typography
        fontSize={{ xs: 11, sm: 12, md: 13 }}
        color="primary.main"
        sx={{ ml: 0.5, mr: 0.5, whiteSpace: "nowrap" }}
        fontWeight={600}
      >
        {area} Sq.ft
      </Typography>
      {isHome && <Typography fontSize={11} sx={{ flexShrink: 0 }}>Total Area</Typography>}
    </Box>
    {!isHome && (
      <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} sx={{ whiteSpace: "nowrap" }}>
        Total Area
      </Typography>
    )}
  </Box>
</Box>

          {!isHome && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={1.5}
              gap={0.5}
            >
              <Box display="flex" alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                <LocationOn sx={{ mr: 0.5, fontSize: 16, color: "#608900", flexShrink: 0 }} />
                <Typography
                  fontSize={{ xs: 11, sm: 12 }}
                  color="#000"
                  sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {address || "Chennai, TamilNadu"}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
                <img
                  src={verified == 1 ? "/assets/images/verified.png" : "/assets/icons/not_verified.png"}
                  width={16}
                  alt="verification"
                  style={{ flexShrink: 0 }}
                />
                <Typography fontSize={{ xs: 11, sm: 12 }} sx={{ ml: 0.5, whiteSpace: "nowrap" }}>
                  {verified == 1 ? "Verified Plot" : "Not Verified"}
                </Typography>
              </Box>
            </Box>
          )}

          {!isHome && (
            <Box display="flex" justifyContent="space-between" mt={1.5} gap={0.5}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontSize={{ xs: 10, sm: 11, md: 12 }} color="#608900" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {formatDate(postedOn)}
                </Typography>
                <Typography fontSize={{ xs: 10, sm: 11, md: 12 }} sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  Posted On
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                <Typography fontSize={{ xs: 10, sm: 11, md: 12 }} color="#608900" sx={{ whiteSpace: "nowrap" }}>
                  {formatDate(lastUpdate)}
                </Typography>
                <Typography fontSize={{ xs: 10, sm: 11, md: 12 }} sx={{ whiteSpace: "nowrap" }}>
                  Last Update
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>

        <Button
          variant="outlined"
          fullWidth
          onClick={(e) => { e.stopPropagation(); navigate(`/plot-marketplace/details/${id}`); }}
          sx={{
            mt: 2,
            borderRadius: 15,
            py: { xs: 0.5, sm: 0.7 },
            fontSize: { xs: 12, sm: 13, md: 14 },
            borderColor: "primary.main",
            opacity: isSold ? 0.65 : 1,
          }}
        >
          View Details
        </Button>
      </Card>
    </>
  );
}